import * as React from 'react';
import { useState, useEffect, useRef } from 'react';

import Table from 'react-bootstrap/Table';
import Button from 'react-bootstrap/Button';

import { CustomProgressBar } from '../CustomProgressBar/CustomProgressBar';
import LoadingSpinner from 'sos-common/js/components/LoadingSpinner/LoadingSpinner';
import { makeTranslator } from '../../utilities/translations';
import { Icon } from '../../entries/components';
import { ButtonModal } from './../ButtonModal/ButtonModal';
import { sha1 } from 'js-sha1';

import {
    formatDateYYYYMMDD,
    formatDiarienummer,
    formatFileSizeWithNormalizedUnit,
} from '../CaseList/Support';

import {
    FileId,
    FileSessionVM,
    FileSessionId,
    FileStatus
} from '../FileAPIClient/Api.types';

import {
    FileAPIClient
} from '../FileAPIClient/FileApiClient';

import {
    FileUploadProps,
    UploadFileTracker,
    FileTrackerStatusEnum,
} from './FileUpload.types';
import './fileupload.scss';

export const FileUpload: React.FC<FileUploadProps> = (props) => {
    const Translate = makeTranslator(props.translation);

    const [fileAPIClient, setFileAPIClient] = useState<FileAPIClient>();
    const [fileTrackers, setFileTrackers] = useState<UploadFileTracker[]>([]);
    const [fileSession, setFileSession] = useState<FileSessionVM>();
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [errorMessageTitle, setErrorMessageTitle] = useState<string | null>(Translate('somethingwentwrong'));
    const [inputHasFocus, setInputHasFocus] = useState(false);
    const [isAuthenticated, setIsAuthenticated] = useState(true);
    const [isConfirmFileDeleteModalOpen, setIsRaderaModalOpen] = useState<boolean>(false);
    const [isErrorModalOpen, setIsErrorModalOpen] = useState<boolean>(false);
    const [isTimeoutModalOpen, setIsTimeoutModalOpen] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isKlarmarkeraModalOpen, setIsKlarmarkeraModalOpen] = useState<boolean>(false);
    const [isPausaModalOpen, setIsPausaModalOpen] = useState<boolean>(false);
    const [isReadyForKlarmarkera, setIsReadyForklarmarkera] = useState<boolean>(false);
    const [isReadyForUpload, setIsReadyForUpload] = useState<boolean>(false);
    const [uploadQueue, setUploadQueue] = useState<number[]>([]);
    const [isQueueActive, setIsQueueActive] = useState<boolean>(false);
    const [isUploadInProgress, setIsUploadInProgress] = useState<boolean>(false);
    const [loadingText, setLoadingText] = useState<string>();
    const [rowIndex, setRowIndex] = useState<number>();
    const [screenReaderMessage, setScreenReaderMessage] = useState<string>('');
    const [redirectToCaseList, setRedirectToCaseList] = useState<boolean>(false);

    // Use ref to track queue active state for real-time checks in async functions
    const isQueueActiveRef = useRef<boolean>(false);

    // Process för att förnya användarsession medan nedladdning pågår
    useEffect(() => {
        if (!isUploadInProgress) {
            return;
        }

        const intervalId = setInterval(() => {
            refreshUserSession();
        }, 15 * 60 * 1000);

        return () => clearInterval(intervalId);
    }, [isUploadInProgress, fileAPIClient]);

    /**
     *
     */
    useEffect(() => {
        initComponent();
    }, []);

    useEffect(() => {
        if (isLoading) {
            return;
        }
        if (isUploadInProgress) {
            window.onbeforeunload = () => {
                //Seems as if the browser will wait for synchronous operations before showing the alert
                //so all fileuploads should be paused correctly by calling handlePauseAllFileUploads before returning
                handlePauseAllFileUploads();
                return ''; //Not possible to customize message in dialog
            };
        } else {
            window.onbeforeunload = null;
        }
    }, [isUploadInProgress, isLoading]);

    /**
     *
     */
    useEffect(() => {
        if (isLoading) {
            return;
        }
        const readyForUpload = uploadQueue.length > 0;
        setIsReadyForUpload(readyForUpload);

        const readyForKlarmarkera = checkReadyForKlarmarkera();
        setIsReadyForklarmarkera(readyForKlarmarkera);
    }, [uploadQueue, fileTrackers, isLoading]);

    // Process för att köra uppladdningskö
    useEffect(() => {
        if (isUploadInProgress || isLoading || !isQueueActive) {
            return;
        }
        if (uploadQueue.length === 0) {
            isQueueActiveRef.current = false;
            setIsQueueActive(false);
        }
        if (uploadQueue.length > 0) {
            processQueue();
        }

    }, [uploadQueue, isQueueActive, isUploadInProgress, isLoading, fileTrackers]);

    const initComponent = async (): Promise<void> => {
        setIsLoading(true);
        try {
            setLoadingText(Translate('fetchingcaseinformation'));

            let apiClient = new FileAPIClient((isAuthenticated: boolean) => {
                setIsAuthenticated(isAuthenticated);
            });
            setFileAPIClient(apiClient);
            await apiClient.isAuthenticated();
            if (!isAuthenticated) {
                throw new Error(Translate('/error/accesscontrol'));
            }

            let id: FileSessionId = { value: props.fileSessionId };
            const fileSessionResponse = await apiClient.getFileSession(id);

            setFileSession(fileSessionResponse);

            try {
                await registerStoredFilesWithFileTracker(apiClient, fileSessionResponse.files);
            } catch {
                throw new Error(Translate('/error/fileregistration'));
            }
        } catch (error) {
            setRedirectToCaseList(true);
            openErrorModal(error instanceof Error ? error.message : String(error));
        } finally {
            setIsLoading(false);
        }
    };

    const addToQueue = (value: UploadFileTracker, index: number) => {
        value.status = FileTrackerStatusEnum.Queued;
        setUploadQueue(prev => [...prev, index]);
    };

    const removeFromQueue = (value: UploadFileTracker, index: number) => {
        const hasProgress = value.currentFilFragmentCount > 0;
        value.status = hasProgress ? FileTrackerStatusEnum.UploadingPaused : FileTrackerStatusEnum.Prepared;
        setUploadQueue(prev => prev.filter(i => i !== index));
    };

    const getQueuePosition = (fileTrackerIndex: number): number => {
        return uploadQueue.indexOf(fileTrackerIndex) + 1;
    };

    const processQueue = async () => {
        const nextFileIndex = uploadQueue[0];

        // Check if the file is still in queue status (could have been dequeued)
        const status = fileTrackers[nextFileIndex]?.status
        if (status === FileTrackerStatusEnum.Queued || status === FileTrackerStatusEnum.Uploading) {
            try {
                setIsUploadInProgress(true);
                await uploadFile(nextFileIndex);
            } catch (error) {
                console.error('Queue processing error:', error);
                // If upload fails, mark file as failed instead of leaving it in limbo
                updateFileTrackerStatus(nextFileIndex, FileTrackerStatusEnum.Error);
                setUploadQueue(prev => prev.slice(1));
            }
            finally {
                setIsUploadInProgress(false);
            }
        }
    }

    const refreshUserSession = async () => {
        if (!isUploadInProgress) {
            return;
        }
        if (!fileAPIClient) {
            // Om vi inte har en fileAPIClient vänta tills de är tillgängliga
            return;
        }

        await fileAPIClient.refreshUserSession();
    }

    const handleStartQueue = () => {
        isQueueActiveRef.current = true;
        setIsQueueActive(true);
    };

    const handleStopQueue = () => {
        isQueueActiveRef.current = false;
        setIsQueueActive(false);
    };

    const registerStoredFilesWithFileTracker = async (
        apiClient: FileAPIClient,
        files: FileId[]
    ) => {
        if (!files || files.length === 0) {
            return;
        }
        if (!apiClient) {
            throw new Error(Translate('/error/somethingwentwrong'));
        }

        const newFileTrackers: UploadFileTracker[] = [];
        const totalFileCount = files.length;

        for (const [index, fileId] of files.entries()) {
            setLoadingText(`${Translate('fetchingfile')} ${index + 1} ${Translate('of')} ${totalFileCount}`);

            const fileInfo = await apiClient.getFile(fileId);
            const newFileTracker = new UploadFileTracker(null, fileInfo);

            if (newFileTracker.status === FileTrackerStatusEnum.UploadingPaused) {
                newFileTracker.infoMsg = Translate('reselectfile');
            }

            newFileTrackers.push(newFileTracker);
        }

        const loadedFileTrackers = [...fileTrackers, ...newFileTrackers];
        setFileTrackers(loadedFileTrackers);
    };

    const handleResumeUpload = (index: number) => {
        const fileTracker = fileTrackers[index];
        fileTracker.clearMessages();
        addToQueue(fileTracker, index);
    };

    const handleDequeueUpload = (index: number) => {
        const fileTracker = fileTrackers[index];
        removeFromQueue(fileTracker, index);
        fileTracker.clearMessages();
    };

    const handleConfirmFileDelete = (index: number) => {
        setRowIndex(index);
        setIsRaderaModalOpen(true);
    };

    const uploadFile = async (fileTrackerIndex: number) => {
        if (!fileSession || !fileAPIClient) {
            throw new Error(Translate('/error/somethingwentwrong'));
        }

        const fileTracker = fileTrackers[fileTrackerIndex];
        const fileInfo = fileTracker.storedFile;
        setScreenReaderMessage(`${Translate('initiatingupload')} ${fileInfo.name}`);
        if (!fileInfo) {
            throw new Error(Translate('/error/somethingwentwrong'));
        }

        updateFileTrackerStatus(fileTrackerIndex, FileTrackerStatusEnum.Uploading);

        setScreenReaderMessage(
            `${Translate('fileuploadof')} ${fileTracker.storedFile!.name} ${Translate('is')} ${fileTracker.progress}% ${Translate('complete')}`
        );

        let filFragmentIndex = 0;
        let startOffset = 0;
        let endOffset = Math.min(fileInfo.fileFragmentSize, fileInfo.size);
        for (; startOffset < fileInfo.size; filFragmentIndex++) {
            // Hantera begärd paus
            if (fileTrackers[fileTrackerIndex].status !== FileTrackerStatusEnum.Uploading) {
                break; // Hoppar ur loopen och avbryter därmed uppladdningen
            }
            if (!isQueueActiveRef.current) {
                break; // Hoppar ur loopen och avbryter därmed uppladdningen
            }

            // Hoppa över redan uppladdade filfragment i händelse av en återupptagen uppladdning
            if (
                fileTrackers[fileTrackerIndex].currentFilFragmentCount > 0 &&
                fileTrackers[fileTrackerIndex].currentFilFragmentCount - 1 >= filFragmentIndex
            ) {
                // Räkna om  offset för filfragment data inför nästa loop
                startOffset = endOffset;
                endOffset = Math.min(endOffset + fileInfo.fileFragmentSize, fileInfo.size);
                continue;
            }

            // Ladda upp filfragmentet
            const filFragmentData = fileTrackers[fileTrackerIndex].browserFile!.slice(startOffset, endOffset);
            if (!fileSession.publicEncryptionKey) {
                throw new Error(Translate('/error/somethingwentwrong'));
            }
            await fileAPIClient.uploadFileFragment(fileInfo.id, filFragmentData, filFragmentIndex, fileSession.publicEncryptionKey);

            // Räkna nya offset värden
            startOffset = endOffset;
            endOffset = Math.min(endOffset + fileInfo.fileFragmentSize, fileInfo.size);

            // Uppdatera framsteg
            fileTrackers[fileTrackerIndex].currentFilFragmentCount = filFragmentIndex + 1;
            setScreenReaderMessage(
                `${Translate('fileuploadof')} ${fileTrackers[fileTrackerIndex].storedFile!.name} ${Translate('is')} ${fileTrackers[fileTrackerIndex].progress}% ${Translate('complete')}`
            );
        }

        if (!isQueueActiveRef.current) {
            return; // Hoppar ur och avbryter därmed uppladdningen
        }

        // Klarmarkera filen om uppladdningen är klar
        if (fileTrackers[fileTrackerIndex].currentFilFragmentCount === fileTrackers[fileTrackerIndex].totalFilFragmentCount) {
            await fileAPIClient.fileTransferDone(fileInfo.id);
            setScreenReaderMessage(
                `${Translate('thefile')} ${fileTrackers[fileTrackerIndex].storedFile!.name} ${Translate('hasbeenuploaded')}.`
            );
            updateFileTrackerStatus(fileTrackerIndex, FileTrackerStatusEnum.Completed);
        }
        setUploadQueue(prev => prev.slice(1));
    };

    const handleKlarmarkeraSession = async (): Promise<void> => {
        if (!fileSession || !fileAPIClient) {
            throw new Error(Translate('/error/somethingwentwrong'));
        }
        setIsKlarmarkeraModalOpen(false);
        setIsLoading(true);
        fileAPIClient.fileSessionDone(fileSession.id).then(
            (success) => {
                if (success.success === false) {
                    setIsTimeoutModalOpen(true);
                }
                window.location.replace(props.pageLinkMyCases);
            },
            (error) => {
                console.log(error);
            }
        );
        setIsLoading(false);
    };

    const onPausaModalClick = async () => {
        setIsPausaModalOpen(false);
    };

    const openErrorModal = (message: string, title: string | null = null) => {
        setErrorMessageTitle(title);
        setErrorMessage(message);
        setIsErrorModalOpen(true);
    };

    const onErrorModalClick = () => {
        setErrorMessageTitle('');
        setErrorMessage('');
        setIsErrorModalOpen(false);

        if (redirectToCaseList) {
            setRedirectToCaseList(false);
            document.location.replace(props.pageLinkMyCases);
        }
    };

    const updateFileTrackerStatus = (index: number, newStatus: FileTrackerStatusEnum) => {
        let updatedFileTrackers = [...fileTrackers];
        updatedFileTrackers[index].status = newStatus;
        setFileTrackers(updatedFileTrackers);
    };

    const deleteFile = async (fileTrackerIndex: number) => {
        const fileInformation = fileTrackers[fileTrackerIndex];
        if (!fileSession || !fileInformation.storedFile || !fileAPIClient) {
            throw new Error(Translate('/error/somethingwentwrong'));
        }
        if (fileInformation.status !== FileTrackerStatusEnum.Queued) {
            setIsLoading(true);
            try {
                await fileAPIClient.deleteFile(fileInformation.storedFile.id);
            } finally {
                setIsLoading(false);
                setScreenReaderMessage(Translate('thefile') + ' ' + fileInformation.name + ' ' + Translate('hasbeendeleted'));
            }
        }

        setFileTrackers((prevFileTracker) => {
            if (fileTrackerIndex >= 0) {
                return prevFileTracker.slice(0, fileTrackerIndex).concat(prevFileTracker.slice(fileTrackerIndex + 1));
            } else {
                return prevFileTracker;
            }
        });
    };

    const containsUnacceptableCharacters = (userSelectedFiles: File[]) => {
        // Kombinerade ogiltiga tecken för Windows och Linux. Justera vid behov.
        const invalidFilenameChars = /[<>:"/\\|?*\x00-\x1F]/g;
        const reservedWindowsNames = [
            'CON',
            'PRN',
            'AUX',
            'NUL',
            'COM1',
            'COM2',
            'COM3',
            'COM4',
            'COM5',
            'COM6',
            'COM7',
            'COM8',
            'COM9',
            'LPT1',
            'LPT2',
            'LPT3',
            'LPT4',
            'LPT5',
            'LPT6',
            'LPT7',
            'LPT8',
            'LPT9',
        ];

        for (const file of userSelectedFiles) {
            const fileName = file.name;

            if (fileName.startsWith('.') || fileName.startsWith(' ')) {
                openErrorModal(`${Translate('thefilename')} "${fileName}" ${Translate('/error/startswithdisallowedchar')}.`);
                setScreenReaderMessage(
                    `${Translate('thefilename')} "${fileName}" ${Translate('/error/startswithdisallowedchar')}.`
                );
                return true;
            }

            if (fileName.endsWith('.') || fileName.endsWith(' ')) {
                openErrorModal(`${Translate('thefilename')} "${fileName}" ${Translate('/error/endswithdisallowedchar')}.`);
                setScreenReaderMessage(
                    `${Translate('thefilename')} "${fileName}" ${Translate('/error/endswithdisallowedchar')}.`
                );
                return true;
            }

            if (fileName.includes('..')) {
                openErrorModal(`${Translate('thefilename')} "${fileName}" ${Translate('/error/twoormoredots')}.`);
                setScreenReaderMessage(`${Translate('thefilename')} "${fileName}" ${Translate('/error/twoormoredots')}.`);
                return true;
            }

            if (fileName.match(invalidFilenameChars)) {
                openErrorModal(`${Translate('thefilename')} "${fileName}" ${Translate('/error/oneormoredisallowedchars')}.`);
                setScreenReaderMessage(
                    `${Translate('thefilename')} "${fileName}" ${Translate('/error/oneormoredisallowedchars')}.`
                );
                return true;
            }

            const nameWithoutExtension = fileName.replace(/\.[^/.]+$/, '').toUpperCase();
            if (reservedWindowsNames.includes(nameWithoutExtension)) {
                openErrorModal(`${Translate('thefilename')} "${fileName}" ${Translate('/error/reservedname')}.`);
                setScreenReaderMessage(`${Translate('thefilename')} "${fileName}" ${Translate('/error/reservedname')}.`);
                return true;
            }

            if (nameWithoutExtension.length > 28) {
                openErrorModal(`${Translate('thefilename')} "${fileName}" ${Translate('/error/exceedsmaxchars')}.`);
                setScreenReaderMessage(`${Translate('thefilename')} "${fileName}" ${Translate('/error/exceedsmaxchars')}.`);
                return true;
            }
        }

        return false;
    };

    const containsUnacceptableFileExtension = (userSelectedFiles: File[]): boolean => {
        const fileExtensions = acceptedFileExtensions();

        for (const file of userSelectedFiles) {
            const parts = file.name.split('.');
            let fileExtension = parts.pop()?.toLowerCase();

            // If the extension is numeric (like .001, .099), get the previous extension
            if (fileExtension && /^\d+$/.test(fileExtension) && parts.length > 0) {
                fileExtension = parts.pop()?.toLowerCase();
            }

            if (!fileExtensions.includes(fileExtension!)) {
                openErrorModal(`${Translate('thefile')} "${file.name}" ${Translate('/error/notallowedfiletype')}!`);
                setScreenReaderMessage(`${Translate('thefile')} "${file.name}" ${Translate('/error/notallowedfiletype')}!`);
                return true;
            }
        }

        return false;
    };

    const containsFileAboveSizeLimit = (userSelectedFiles: File[]): boolean => {
        const fileSizeLimitInBytes = 42949672961; // Aktuellt gränsvärde 40GB + 1

        for (const file of userSelectedFiles) {
            if (file.size > fileSizeLimitInBytes) {
                openErrorModal(`${Translate('thefile')} "${file.name}" ${Translate('/error/largerthanmaxlimit')}!`);
                setScreenReaderMessage(`${Translate('thefile')} "${file.name}" ${Translate('/error/largerthanmaxlimit')}!`);
                return true;
            }
        }

        return false;
    };

    const containsFileAlreadySelectedOrUploaded = (userSelectedFiles: File[]): boolean => {
        for (const file of userSelectedFiles) {
            // Man skall bara kunna välja en fil om den inte redan är registrerad alternativt om den är registrerad men har status skilt från Completed och saknas browserFile
            const notSelecteable = fileTrackers.some(
                (x) => x.name === file.name && (x.status === FileTrackerStatusEnum.Completed || x.browserFile != null)
            );
            if (notSelecteable) {
                openErrorModal(`${Translate('thefile')} "${file.name}" ${Translate('/error/alreadyselectedoruploaded')}!`);
                setScreenReaderMessage(
                    `${Translate('thefile')} "${file.name}" ${Translate('/error/alreadyselectedoruploaded')}!`
                );
                return true;
            }
        }

        return false;
    };

    const acceptedFileExtensions = (): string => {
        if (fileSession && fileSession.allowedFileTypes) {
            if (fileSession.allowedFileTypes.length > 0) {
                return fileSession.allowedFileTypes.map((ext) => `.${ext}`).join(', ');
            }
        }
        return ''; // Return empty string if anvandarSession or any nested property is undefined
    };

    const checkReadyForKlarmarkera = () => {
        const relevant = fileTrackers
            .filter(x => x.status !== FileTrackerStatusEnum.Sent);

        if (relevant.length <= 0) {
            return false;
        }
        const someNotDone = relevant
            .some((s) => s.status !== FileTrackerStatusEnum.Completed);
        return !someNotDone;
    };

    const handleDeleteFile = async (fileTrackerIndex: number) => {
        setIsRaderaModalOpen(false);
        await deleteFile(fileTrackerIndex);
    };

    const handleShowKlarmarkeraSessionModal = async () => {
        setIsKlarmarkeraModalOpen(true);
    };

    const handlePauseAllFileUploads = () => {
        setIsQueueActive(false);
    };

    const closeKlarmarkeraSessionModal = () => {
        setIsKlarmarkeraModalOpen(false);
    };

    const closeRaderaFilModal = () => {
        setIsRaderaModalOpen(false);
    };

    const closePauseModal = () => {
        setIsPausaModalOpen(false);
    };

    const registerSelectedFilesWithFileTracker = async (files: File[]) => {
        setIsLoading(true);
        if (!fileSession || !fileAPIClient) {
            throw new Error(Translate('/error/somethingwentwrong'));
        }

        const newFileTrackers: UploadFileTracker[] = [];
        for (const file of files) {
            let existingFileTracker = fileTrackers.find(
                (x) => x.name === file.name && (x.status === FileTrackerStatusEnum.UploadingPaused || x.status === FileTrackerStatusEnum.Error)
            );

            if (existingFileTracker) {
                setScreenReaderMessage(
                    `${Translate('preparefile')} ${existingFileTracker.name}`
                );
                existingFileTracker.clearMessages();
                existingFileTracker.infoMsg = `${Translate('preparefile')}`
                existingFileTracker.status = FileTrackerStatusEnum.Preparing;
                existingFileTracker.browserFile = file;
                const checksum = await calculateChecksumForFile(existingFileTracker);
                if (checksum === existingFileTracker.checksum) {
                    existingFileTracker.clearMessages();
                    const hasProgress = existingFileTracker.currentFilFragmentCount > 0;
                    existingFileTracker.status = hasProgress ? FileTrackerStatusEnum.UploadingPaused : FileTrackerStatusEnum.Prepared;
                }
                else {
                    existingFileTracker.browserFile = null;
                    existingFileTracker.status = FileTrackerStatusEnum.Error;
                    existingFileTracker.infoMsg = Translate('reselectfile');
                    existingFileTracker.errorMsg = `${Translate('/error/filemissmatch')}`
                }
            } else {
                // Skapa en ny filspårare
                const newFileTracker = new UploadFileTracker(file, null);
                newFileTrackers.push(newFileTracker);
            }
        }

        // Slå sammansamman den ursprungliga listan av filspårare med listan över nya filspårare
        const loadedFileTrackers = [...fileTrackers, ...newFileTrackers];
        setFileTrackers(loadedFileTrackers);

        for (const newFileTracker of newFileTrackers) {
            // Skapa en fil på serversidan om den inte redan finns
            let fileInfo = newFileTracker.storedFile
            if (!fileInfo) {
                const size = newFileTracker.size;

                newFileTracker.infoMsg = `${Translate('preparefile')}`
                newFileTracker.status = FileTrackerStatusEnum.Preparing;
                setScreenReaderMessage(
                    `${Translate('preparefile')} ${newFileTracker.name}`
                );

                try {
                    newFileTracker.checksum = await calculateChecksumForFile(newFileTracker);
                    newFileTracker.status = FileTrackerStatusEnum.Prepared;
                } catch {
                    newFileTracker.infoMsg = `${Translate('/error/fileregistration')}`
                }

                fileInfo = await fileAPIClient.createFile(fileSession.id, newFileTracker.name, size, newFileTracker.checksum);

                newFileTracker.storedFile = fileInfo;

                const index = loadedFileTrackers.findIndex(x => x === newFileTracker);
                addToQueue(loadedFileTrackers[index], index);
            }
        }

        setIsLoading(false);
    };

    const calculateChecksumForFile = async (fileTracker: UploadFileTracker): Promise<string> => {
        // Use js-sha1 for true streaming/incremental hashing
        const hasher = sha1.create();
        const fileForChecksumCalc = fileTracker.browserFile;

        const stream = fileForChecksumCalc.stream();
        const reader = stream.getReader();

        const totalSize = fileTracker.size;
        let processedBytes = 0;
        let chunkCount = 0;

        let base64Hash: string | null = null;
        try {
            while (true) {
                const { done, value } = await reader.read();

                if (done) {
                    break;
                }

                // Update the hash incrementally with each chunk
                // js-sha1 processes this without keeping all chunks in memory
                hasher.update(value);

                // Update progress
                processedBytes += value.length;
                chunkCount++;
                if (totalSize > 0 && chunkCount % 10 === 0) {
                    fileTracker.checksumProgress = Math.round((processedBytes / totalSize) * 100);
                    await new Promise(resolve => setTimeout(resolve, 0));
                    setScreenReaderMessage(
                        `${Translate('preparefile')} ${fileTracker.name} ${Translate('is')} ${fileTracker.checksumProgress}% ${Translate('done')}`
                    );
                }
            }

            // Get the final hash as base64
            const hashHex = hasher.hex();

            // Convert hex to base64 to match the format used by calculateDownloadChecksum
            const hashBytes = new Uint8Array(hashHex.match(/.{1,2}/g)!.map(byte => parseInt(byte, 16)));
            base64Hash = btoa(String.fromCharCode(...hashBytes));

            fileTracker.infoMsg = ``
        } finally {
            reader.releaseLock();
        }
        return base64Hash;
    };

    const handleInputFocus = (event: React.FocusEvent<HTMLInputElement>) => {
        setInputHasFocus(true);
    };

    const handleInputBlur = (event: React.FocusEvent<HTMLInputElement>) => {
        setInputHasFocus(false);
    };

    const handleFileSelectionWithInputField = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFiles = Array.from(event.target.files || []);

        // Rensa inför nästa filval
        event.target.value = '';

        // Avbryt om inga filer är valda
        if (!selectedFiles.length) return;

        // Avbryt om filnamnen innehåller ogiltiga tecken
        if (containsUnacceptableCharacters(selectedFiles)) {
            return;
        }

        // Avbryt om något av filerna inte är av rätt filtyp
        if (containsUnacceptableFileExtension(selectedFiles)) {
            return;
        }

        // Avbryt om minst en av de valda filerna är större än maximal filstorlek
        if (containsFileAboveSizeLimit(selectedFiles)) {
            return;
        }

        // Avbryt om minst en av filerna redan är vald, på väg att bli uppladdad eller är uppladdad
        if (containsFileAlreadySelectedOrUploaded(selectedFiles)) {
            return;
        }

        // Registrera filerna med FileTracker
        await registerSelectedFilesWithFileTracker(selectedFiles);
    };

    return (
        <>
            {props.pageHeading && <h1>{fileSession?.arende.title}</h1>}
            {<p className="">{formatDiarienummer(fileSession?.arende.diarienummer)}</p>}
            {props.pageBody && <p dangerouslySetInnerHTML={{ __html: props.pageBody }}></p>}
            {<h2 className="">{Translate('sidtitel')}</h2>}

            {/* <div className="file-selector">
                <button type="button" disabled={isUploadInProgress} aria-label={Translate('filval')} className='file-selector__button' onClick={() => handleFileSelectionWithFilePicker()}>
                    <Icon iconType="fontawesome/paperclip" />
                    {Translate('filval')}
                </button>
            </div> */}
            <div className="file-selector">
                <input
                    disabled={isLoading || isUploadInProgress}
                    id="fileInput"
                    type="file"
                    accept={acceptedFileExtensions()}
                    multiple
                    onChange={handleFileSelectionWithInputField}
                    onFocus={handleInputFocus}
                    onBlur={handleInputBlur}
                    className={'file-selector__input'}
                />
                <label
                    {...(inputHasFocus && { 'data-focus-visible-added': '' })}
                    role="button"
                    htmlFor="fileInput"
                    className={`file-selector__label ${inputHasFocus ? 'focus-visible' : ''}`}>
                    <Icon iconType="fontawesome/paperclip" />
                    {Translate('filval')}
                </label>
            </div>
            <div aria-busy={isLoading} className="file-tracker">
                {fileTrackers.length > 0 && (
                    <Table className="file-tracker-table" aria-label={Translate('fileuploads')}>
                        <thead>
                            <tr>
                                <th className="file-tracker__table-header file-tracker__table-header--name">
                                    {Translate('filnamn')}
                                </th>
                                <th className="file-tracker__table-header file-tracker__table-header--size">
                                    {Translate('filstorlek')}
                                </th>
                                <th
                                    className="file-tracker__table-header file-tracker__table-header--status">
                                    {Translate('status')}
                                </th>
                                <th
                                    aria-label={Translate('action')}
                                    className="file-tracker__table-header file-tracker__table-header--action file-tracker__table-header--right">
                                </th>
                                <th
                                    aria-label={Translate('deletefile')}
                                    className="file-tracker__table-header file-tracker__table-header--delete file-tracker__table-header--right">
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {fileTrackers.map((fileTracker: UploadFileTracker, fileTrackerIndex) => (
                                <React.Fragment key={fileTrackerIndex}>
                                    <tr className={(fileTracker.status === FileTrackerStatusEnum.Sent ? "file-tracker__table-row--disabled " : "")  + "file-tracker__table-row"}>
                                        <td>{fileTracker.name}</td>
                                        <td>{formatFileSizeWithNormalizedUnit(fileTracker.size)}</td>
                                        <td className="file-tracker__table-cell">
                                            {fileTracker.status === FileTrackerStatusEnum.Completed && (
                                                <span
                                                    aria-live="polite"
                                                    className="file-tracker__icon file-tracker__icon--status-completed">
                                                    <Icon iconType="fontawesome/check-circle" />
                                                    {Translate('isuploaded')}
                                                </span>
                                            )}
                                            {fileTracker.status === FileTrackerStatusEnum.Queued && (
                                                <span className="file-tracker__icon" aria-live="polite">
                                                    <Icon iconType="fontawesome/clock" />
                                                    {Translate('queuedforupload') || 'Queued for upload'}: {getQueuePosition(fileTrackerIndex)}
                                                </span>
                                            )}
                                            {fileTracker.status === FileTrackerStatusEnum.Sent && (
                                                <span
                                                    aria-live="polite"
                                                    className="file-tracker__icon file-tracker__icon--status-completed">
                                                    <Icon iconType="fontawesome/check-circle" />
                                                    {Translate('issent') || 'Is sent'} {formatDateYYYYMMDD(fileTracker.storedFile.processed)}
                                                </span>
                                            )}
                                        </td>
                                        <td className="file-tracker__table-cell--right">
                                            {(fileTracker.status === FileTrackerStatusEnum.UploadingPaused || fileTracker.status === FileTrackerStatusEnum.Prepared) &&
                                                !fileTracker.hasInfo && (
                                                    <button
                                                        type="button"
                                                        aria-disabled={isLoading}
                                                        disabled={isLoading}
                                                        className="file-tracker__pause"
                                                        onClick={() => handleResumeUpload(fileTrackerIndex)}>
                                                        <span>{Translate('resumefileupload')}</span>
                                                    </button>
                                                )}
                                            {(fileTracker.status === FileTrackerStatusEnum.Uploading) &&
                                            !fileTracker.hasInfo && (
                                                <button
                                                    type="button"
                                                    aria-disabled={isLoading}
                                                    disabled={isLoading}
                                                    className="file-tracker__pause"
                                                    onClick={() => handleDequeueUpload(fileTrackerIndex)}>
                                                    <span>{Translate('pausefileupload')}</span>
                                                </button>
                                                )}
                                            {fileTracker.status === FileTrackerStatusEnum.Queued && (
                                                <button
                                                    title={Translate('removefromqueue') || 'Remove from queue'}
                                                    disabled={isLoading}
                                                    aria-disabled={isLoading}
                                                    type="button"
                                                    className="down-file-tracker__dequeue"
                                                    aria-label={`${Translate('removefromqueue') || 'Remove from queue'} ${fileTracker.name}`}
                                                    onClick={() => handleDequeueUpload(fileTrackerIndex)}>
                                                    <span>{Translate('removefromqueue') || 'Remove from queue'}</span>
                                                </button>
                                            )}
                                        </td>
                                        <td className="file-tracker__table-cell--right">
                                            {!(isUploadInProgress || fileTracker.status === FileTrackerStatusEnum.Sent) && (
                                                <button
                                                    title={Translate('removefile')}
                                                    type="button"
                                                    aria-disabled={isLoading}
                                                    disabled={isLoading}
                                                    className="file-tracker__delete"
                                                    aria-label={`${Translate('removethefile')} ${fileTracker.name}`}
                                                    onClick={() => handleConfirmFileDelete(fileTrackerIndex)}>
                                                    <span><Icon iconType="fontawesome/trash-alt" /></span>
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td colSpan={5} className="file-tracker__progress-wrapper">
                                            {fileTracker.status === FileTrackerStatusEnum.Preparing && (
                                                <div className="file-tracker__progress-container" aria-hidden="true">
                                                    <div className="file-tracker__progress-bar">
                                                        <CustomProgressBar
                                                            animated={fileTracker.status === FileTrackerStatusEnum.Preparing}
                                                            now={fileTracker.checksumProgress}
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                            {fileTracker.progress > 0 && fileTracker.status !== FileTrackerStatusEnum.Completed && (
                                                <div className="file-tracker__progress-container" aria-hidden="true">
                                                    <div className="file-tracker__progress-bar">
                                                        <CustomProgressBar
                                                            animated={fileTracker.status === FileTrackerStatusEnum.Uploading}
                                                            now={fileTracker.progress}
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                            {fileTracker.hasInfo && (
                                                <div className="file-tracker__infopanel">
                                                    <Icon iconType="fontawesome/exclamation-circle-solid" />
                                                    {fileTracker.infoMsg}
                                                </div>
                                            )}
                                            {fileTracker.hasError && (
                                                <div className="file-tracker__errorpanel">
                                                    <Icon iconType="fontawesome/exclamation-circle-solid" />
                                                    {fileTracker.errorMsg}
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                </React.Fragment>
                            ))}
                        </tbody>
                    </Table>
                )}

                {isLoading && (
                    <div className="flex-center">
                        <LoadingSpinner darkColor={true} cssClasses="file-tracker__spinner" />
                    </div>
                )}

                {!isLoading && fileTrackers.length === 0 && (
                    <p className={'file-tracker__nofiles'}>{Translate('nofileselected')}</p>
                )}
                {isLoading && fileTrackers.length === 0 && <p className={'file-tracker__loadingfiles'}>{loadingText}</p>}
                <div className="sr-only" aria-live="assertive">
                    {screenReaderMessage}
                </div>
            </div>

            <div className="file-actions m-b-2">
                {!isQueueActive && (
                    <Button
                        className="button file-actions__button--left"
                        aria-disabled={isUploadInProgress || !isReadyForUpload}
                        disabled={isUploadInProgress || !isReadyForUpload}
                        onClick={handleStartQueue}>
                        {Translate('start')}
                    </Button>
                )}
                {isQueueActive && (
                    <Button
                        className="button file-actions__button--left"
                        aria-disabled={!isUploadInProgress}
                        disabled={!isUploadInProgress}
                        onClick={handleStopQueue}>
                        {Translate('pause')}
                    </Button>
                )}
                <Button
                    className="button file-actions__button--right"
                    aria-disabled={isLoading || isUploadInProgress || !isReadyForKlarmarkera}
                    disabled={isLoading || isUploadInProgress || !isReadyForKlarmarkera}
                    onClick={handleShowKlarmarkeraSessionModal}>
                    {Translate('send')}
                </Button>
            </div>

            <ButtonModal
                isModalOpen={isConfirmFileDeleteModalOpen}
                passData={handleDeleteFile}
                buttonText={''}
                buttonCss={'button'}
                modalAction={''}
                modalHeading={Translate('deletefile')}
                modalBody={
                    fileTrackers[rowIndex!] &&
                    `${Translate('deletefileconfirmation')} "${fileTrackers[rowIndex!].name}"? ${Translate('cantundo')}.`
                }
                modalCancelText={Translate('abort')}
                modalCancelCss={'button'}
                modalActionText={Translate('yes')}
                modalActionCss={'button'}
                closeConfirm={closeRaderaFilModal}
                rowIndex={rowIndex}></ButtonModal>

            <ButtonModal
                isModalOpen={isKlarmarkeraModalOpen}
                passData={handleKlarmarkeraSession}
                buttonText={''}
                buttonCss={'button'}
                modalAction={''}
                modalHeading={Translate('filesuploaded')}
                modalBody={Translate('markasdoneconfirmation')}
                isModalBodyHtml={true}
                modalCancelText={Translate('no')}
                modalCancelCss={'button'}
                modalActionText={Translate('yes')}
                modalActionCss={'button'}
                closeConfirm={closeKlarmarkeraSessionModal}
                rowIndex={rowIndex}></ButtonModal>

            <ButtonModal
                isModalOpen={isPausaModalOpen}
                passData={onPausaModalClick}
                buttonText={''}
                buttonCss={'button'}
                modalAction={''}
                modalHeading={Translate('wanttopausefileupload')}
                modalBody={Translate('navigatepausemessage')}
                modalCancelText={Translate('abort')}
                modalCancelCss={'button'}
                modalActionText={Translate('pausefileupload')}
                modalActionCss={'button'}
                closeConfirm={closePauseModal}
                rowIndex={rowIndex}></ButtonModal>

            <ButtonModal
                isModalOpen={isErrorModalOpen}
                passData={onErrorModalClick}
                modalHeading={errorMessageTitle ?? Translate('somethingwentwrong')}
                modalBody={errorMessage}
                modalActionText={Translate('close')}
                modalActionCss={'button'}
                closeConfirm={onErrorModalClick}></ButtonModal>

            <ButtonModal
                isModalOpen={isTimeoutModalOpen}
                passData={undefined}
                okAction={() => document.location.replace(props.pageLinkMyCases)}
                modalHeading={Translate('somethingwentwrong')}
                modalBody={Translate('/error/sessiontimeout')}
                modalActionText={Translate('close')}
                modalActionCss={'button'}
                closeConfirm={() => document.location.replace(props.pageLinkMyCases)}
            />
        </>
    );
};
